import { defineComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'
import { OrgGroupsActions, OrgGroupsGetters } from '~/store/control-panel/groups'
import { TeamMemberRoleChoices } from '~/types/types'
import MutationFailedError from '~/utils/mutationFailedError'
import TEAM_PERMS from '~/utils/teamPerms'

export default defineComponent({
  name: 'AddTeamToGroupMixin',

  data() {
    return {
      refetch: true,
      TEAM_PERMS,
      DEFAULT_TEAM_GROUP_ROLE: TeamMemberRoleChoices.Member
    }
  },

  computed: {
    ...mapGetters('control-panel/groups', {
      groupTeamsToAdd: OrgGroupsGetters.TEAMS_TO_ADD
    })
  },

  methods: {
    ...mapActions('control-panel/groups', {
      fetchTeamsToAdd: OrgGroupsActions.FETCH_TEAMS_TO_ADD,
      addTeamToGroup: OrgGroupsActions.ADD_TEAM_TO_GROUP,
      updateGroupTeamRole: OrgGroupsActions.UPDATE_GROUP_TEAM_ROLE
    }),

    /**
     * Dummy function to be overridden on pages that need to refetch data locally within page
     *
     * @param  {{ refetchAddTeams?: boolean }} refetchParams
     * @returns Promise<void>
     */
    // skipcq: JS-0387
    async refetchData(refetchParams?: { refetchAddTeams?: boolean }): Promise<void> {
      // eslint-disable-line
      // Do nothing.
    },

    async addTeamToOrgGroup(
      groupId: string,
      teamId: string,
      role: TeamMemberRoleChoices
    ): Promise<void> {
      try {
        const response = await this.addTeamToGroup({ groupId, teamId, role })
        if (!response) {
          throw new MutationFailedError('An error occurred while adding team, please try again.')
        }
        this.$toast.success('Team added to group successfully.')
      } catch (e) {
        this.$toast.danger((e as Error).message)
      } finally {
        this.refetch = true
        this.$fetch()
        this.$emit('refetch')
      }
    },

    async updateTeamRole(
      groupId: string,
      teamId: string,
      role: TeamMemberRoleChoices
    ): Promise<void> {
      try {
        const response = await this.updateGroupTeamRole({
          groupId,
          teamId,
          role
        })
        if (!response) {
          throw new MutationFailedError(
            'An error occurred while updating team role, please try again.'
          )
        }
        this.$toast.success('Successfully updated role for team.')
      } catch (e) {
        this.$toast.danger((e as Error).message)
      } finally {
        this.refetch = true
        this.$fetch()
        this.$emit('refetch', { refetchAddTeams: true })
        if (this.refetchData) await this.refetchData({ refetchAddTeams: true })
      }
    }
  }
})
